import React from 'react';
import { Link } from 'react-router-dom';

const Brokers = () => {
  const benefits = [
    { icon: '📄', title: 'Automated RFPs', desc: 'Generate vendor shortlists, benefit designs, and pricing comparisons in minutes.' },
    { icon: '📊', title: 'Multi-client scale', desc: 'Centralize benchmarks and templates; reuse insights across similar segments.' },
    { icon: '🤝', title: 'Stickier relationships', desc: "Always-on monitoring and proactive guidance turn you into the client's daily copilot." }
  ];

  const useCases = [
    { icon: '🎯', title: 'Prospecting Intelligence', desc: 'Identify target accounts, estimate savings, and generate tailored outreach angles.' },
    { icon: '⚖️', title: 'Compliance & Risk Briefs', desc: 'Auto-generate ERISA/ACA updates and policy recommendations per client industry.' },
    { icon: '📑', title: 'Executive-ready Deliverables', desc: 'On-brand decks, vendor scorecards, and renewal narratives — one click away.' },
    { icon: '📈', title: 'Benchmarking at Scale', desc: 'Compare design, utilization, and PMPM vs. peers across markets and sizes.' },
    { icon: '🏢', title: 'Vendor Landscape', desc: 'Curate categories, evaluate ROI, and recommend best-fit partners programmatically.' },
    { icon: '✉️', title: 'Campaigns & Nurture', desc: "Generate outreach cadences and proposals personalized to a prospect's pain." }
  ];

  const packages = [
    {
      name: 'Starter',
      desc: 'For boutique teams validating AI workflows.',
      features: ['Up to 5 clients', 'Core benchmarks', 'Auto-reports & briefs']
    },
    {
      name: 'Growth',
      desc: 'For firms scaling across verticals and regions.',
      features: ['Up to 25 clients', 'Custom templates & branding', 'RFP generation & scorecards'],
      popular: true
    },
    {
      name: 'Enterprise',
      desc: 'For national brokerages & alliances.',
      features: ['Unlimited clients', 'Advanced security & SSO', 'Private data pipelines']
    }
  ];

  const steps = [
    { num: '1', title: 'Import artifacts', desc: 'Claims, design docs, vendor invoices, renewal decks — any format.' },
    { num: '2', title: 'Choose playbooks', desc: 'RFP, renewal, or optimization paths that match client goals.' },
    { num: '3', title: 'Deliver value', desc: 'Auto-build narratives, visuals, and recommendations — ready to present.' }
  ];

  return (
    <div className="page-content">
      {/* Hero */}
      <section className="hero">
        <div className="hero-content">
          <p className="hero-subtitle">Solutions for Brokers & Consultants</p>
          <h1 className="hero-title">
            Win Business. Keep It. <span className="gradient-text">Scale It.</span>
          </h1>
          <p className="hero-description">
            Deliver differentiated insights, automate proposals, and produce on-brand deliverables — faster than ever.
          </p>
          <div className="hero-buttons">
            <Link to="/contact" className="btn btn-primary btn-large">Book a Partner Demo</Link>
            <a href="#packages" className="btn btn-secondary btn-large">View Packages</a>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section">
        <div className="section-center" style={{ marginBottom: '4rem' }}>
          <h2 className="section-heading">
            Grow revenue with <span className="gradient-text">less effort</span>
          </h2>
        </div>

        <div className="benefits-grid">
          {benefits.map((benefit, i) => (
            <div key={i} className="benefit-card">
              <div className="benefit-icon">{benefit.icon}</div>
              <h3 className="benefit-title">{benefit.title}</h3>
              <p className="benefit-desc">{benefit.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Use Cases */}
      <section className="section">
        <div className="section-center" style={{ marginBottom: '4rem' }}>
          <h2 className="section-heading">
            What firms use <span className="gradient-text">Avenir AI</span> for
          </h2>
        </div>

        <div className="use-cases-grid">
          {useCases.map((useCase, i) => (
            <div key={i} className="use-case-card">
              <div className="use-case-icon">{useCase.icon}</div>
              <h3 className="use-case-title">{useCase.title}</h3>
              <p className="use-case-desc">{useCase.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Packages */}
      <section className="section" id="packages">
        <div className="section-center" style={{ marginBottom: '4rem' }}>
          <h2 className="section-heading">
            Partner <span className="gradient-text">packages</span>
          </h2>
        </div>

        <div className="packages-grid">
          {packages.map((pkg, i) => (
            <div key={i} className={`package-card ${pkg.popular ? 'package-popular' : ''}`}>
              {pkg.popular && <span className="package-badge">Most Popular</span>}
              <h3 className="package-name">{pkg.name}</h3>
              <p className="package-desc">{pkg.desc}</p>
              <ul className="package-features">
                {pkg.features.map((feature, j) => (
                  <li key={j}>✓ {feature}</li>
                ))}
              </ul>
              <Link to="/contact" className="btn btn-primary" style={{ width: '100%', textAlign: 'center' }}>
                Get Started
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Steps */}
      <section className="section">
        <div className="section-center" style={{ marginBottom: '4rem' }}>
          <h2 className="section-heading">
            Spin up a client in <span className="gradient-text">3 steps</span>
          </h2>
        </div>

        <div className="steps-grid steps-grid-3">
          {steps.map((step, i) => (
            <div key={i} className="step-card">
              <div className="step-num">{step.num}</div>
              <h3 className="step-title">{step.title}</h3>
              <p className="step-desc">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Ecosystem */}
      <section className="section">
        <div className="section-center">
          <h2 className="section-heading">
            Built for your <span className="gradient-text">stack</span>
          </h2>
          <p className="section-description">
            Integrates with carriers, TPAs, HRIS, and your firm's knowledge base for repeatable excellence.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="cta-wrapper">
          <div className="cta-glow"></div>
          <div className="cta-box">
            <h2 className="cta-title">See Avenir AI for Brokers & Consultants</h2>
            <p className="cta-desc">Stand out in every renewal and RFP with AI-generated insights and deliverables.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary btn-large">Become a Partner</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Brokers;